import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import { extractYearFromString } from 'src/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { TranslateDataKeysService } from 'src/shared/services/translate-data-keys.service';

@Component({
  selector: 'atom-heat-map-chart',
  templateUrl: './heat-map-chart.component.html',
  styleUrls: ['./heat-map-chart.component.scss'],
})
export class HeatMapChartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private root!: am5.Root | undefined;
  private chart!: am5xy.XYChart;
  private yAxis!: am5xy.CategoryAxis<am5xy.AxisRenderer>;
  private xAxis!: am5xy.CategoryAxis<am5xy.AxisRenderer>;
  public exporting!:am5plugins_exporting.Exporting;

  public chartHeight: number = 500; // altura base por defecto
  private readonly baseRowHeight = 40; // altura por cada fila de datos
  private readonly minHeight = 500; // altura mínima del gráfico
  private readonly maxHeight = 12000; // altura máxima del gráfico


  @Input() chartData: Record<string, string | number >[] = []; // Datos a graficar (debe ser un array de objetos JSON)
  @Input() categoryYField:string = ""; // Nombre del campo para las categorías en el eje Y
  @Input() categoryXField:string = "";  // Nombre del campo para las categorías en el eje X
  @Input() minColor:string = "#fffb77"; // Color para el valor mínimo en el mapa de calor
  @Input() maxColor:string = "#fe131a";  // Color para el valor máximo en el mapa de calor
  @Input() chartId: string = 'chart-' + Math.random().toString(36).substr(2, 9); // Genera un id único para renderizar el gráfico
  @Input() fileName: string = ''; // Nombre del archivo al exportar
  @Input() language:string = ""; // Idioma de la aplicación
  @Input() valueRangeMin?: number; // Valor numérico mínimo para la escala de color
  @Input() valueRangeMax?: number;  // Valor numérico máximo para la escala de color
  @Input() xAxisTitle?: string; // Título para el eje X
  @Input() yAxisTitle?: string; // Título para el eje Y

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object, // Inyecta el identificador de la plataforma (navegador o servidor)
    private zone: NgZone,  // Inyecta NgZone para manejar la detección de cambios y ejecutar código fuera del contexto de Angular
    private translateService: TranslateService, private translateDataKeysService: TranslateDataKeysService,
  ) {}

  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) { // Verifica si la aplicación se está ejecutando en el navegador
      this.zone.runOutsideAngular(() => {  // Ejecuta el código fuera del contexto de Angular para evitar la detección de cambios innecesaria
        f(); // Llama a la función proporcionada como parametro
      });
    }
  }

  ngOnInit(): void {}

  // Método llamado después de que la vista del componente se haya inicializado
  ngAfterViewInit() {
    this.browserOnly(() => {   // Ejecuta el código solo en el navegador
      this.root = am5.Root.new(this.chartId);  // Crea una nueva instancia de 'am5.Root' asociada al elemento con un id único (contenedor donde se construirá el gráfico)
      this.renderChart();  // Llama al método para configurar y renderizar el gráfico
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.root) return;

    if (changes['chartData'] && !changes['language']) {
      this.updateChartData(); // Actualiza los datos del gráfico con los nuevos valores proporcionados
    }

    if (changes['language']) {
      this.root.dispose();
      this.root = undefined;

      // Vuelve a renderizar el gráfico
      this.browserOnly(() => {
        this.root = am5.Root.new(this.chartId);
        this.renderChart();
      });
    }
  }

  ngOnDestroy() {
    this.browserOnly(() => {   // Ejecuta el código solo en el navegador
      if (this.root) {   // Verifica si 'this.root' está definido
        this.root.dispose(); // Libera los recursos asociados a la instancia de 'am5.Root' para evitar fugas de memoria
      }
    });
  }

  private applyCustomChartTheme () {
    if(!this.root) return;

    // Crea un nuevo tema utilizando la instancia raíz del gráfico (`this.root`)
    // Este tema personalizado (`myTheme`) se puede usar para configurar estilos específicos en el gráfico
    const myTheme = am5.Theme.new(this.root);

    myTheme.rule('AxisLabel').setAll({ // Establece una regla para las etiquetas de los ejes (`AxisLabel`, tanto el eje X como Y)
      fontSize: '0.9em',  // Tamaño de la fuente de las etiquetas de los ejes
    });

    // Aplica los temas: animado, personalizado y responsive
    this.root.setThemes([
      am5themes_Animated.new(this.root),
      myTheme,
      am5themes_Responsive.new(this.root)
    ]);
  }

  private uniqueValues(field:string):(string | number)[]{
    /* Esta función toma un campo específico de los datos y devuelve una lista de valores únicos
    encontrados en ese campo, eliminando los duplicados */
    return Array.from(new Set(this.chartData.map(row => row[field]))); // Obtiene los valores únicos de un campo de los datos del gráfico
  }

  private calculateChartHeight(): void {
    if (!this.chartData || !this.categoryYField) return;

    // Obtener cantidad de accesos únicos
    const uniqueAccesses = this.uniqueValues(this.categoryYField).length;

    // Calcular altura basada en la cantidad de accesos
    const calculatedHeight = uniqueAccesses * this.baseRowHeight;

    // Asegurar que la altura esté entre los límites establecidos
    this.chartHeight = Math.max(
      this.minHeight,
      Math.min(calculatedHeight, this.maxHeight)
    );
  }

  private renderChart() {
    if (!this.root) return; // Si 'this.root' no esta definido, detiene la ejecución del método

    this.calculateChartHeight();

    this.applyCustomChartTheme(); // Llama al método para establecer los temas personalizados al gráfico

    // Crea y agrega un gráfico XY al contenedor de la raíz del gráfico
    this.chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        wheelX: 'panX', // Permite desplazar el gráfico horizontalmente con la rueda del mouse
        wheelY: 'zoomX',  // Permite hacer zoom horizontal con la rueda del mouse
        pinchZoomX: true, // Habilita el zoom con pellizco en el eje X para pantallas táctiles
        layout: this.root.verticalLayout // Establece que los elementos deben apilarse verticalmente (uno debajo del otro), el gráfico se colocará siguiendo esa disposición vertical
      })
    );

    // Crea un renderizador para el eje Y con configuraciones personalizadas
    let yRenderer = am5xy.AxisRendererY.new(this.root, {
      visible: false,  // Eje Y no visible
      minGridDistance: 20,  // Distancia mínima entre líneas de cuadrícula
      inversed: true,  // Invertir el eje Y
      minorGridEnabled: true,  // Habilitar cuadrícula menor

    });

    // Crea y configura el eje Y de tipo categoría
    this.yAxis = this.chart.yAxes.push(am5xy.CategoryAxis.new(this.root, {
      maxDeviation: 0,  // Sin desviación máxima
      renderer: yRenderer,  // Asocia el renderizador configurado
      categoryField: this.categoryYField  // Campo de categoría para el eje Y
    }));

    // Crea un renderizador para el eje X con configuraciones personalizadas
    let xRenderer = am5xy.AxisRendererX.new(this.root, {
      visible: false,  // Eje X no visible
      minGridDistance: 30,  // Distancia mínima entre líneas de cuadrícula
      opposite: true,  // Coloca el eje X en la parte superior
      minorGridEnabled: true  // Habilitar cuadrícula menor
    });

    // Crea y configura el eje X de tipo categoría
    this.xAxis = this.chart.xAxes.push(am5xy.CategoryAxis.new(this.root, {
      renderer: xRenderer,  // Asocia el renderizador configurado
      categoryField: this.categoryXField  // Campo de categoría para el eje X
    }));

   // Configura el título para el eje X
   if(this.xAxisTitle) {
      const yearMatch = extractYearFromString(this.xAxisTitle);
      let nameBase = yearMatch ? this.xAxisTitle.replace(yearMatch, '').trim() : this.xAxisTitle; // Elimina el año, si no encuentra año, asigna el título original
      let xTitle = this.translateService.instant(`CHARTS.${nameBase}`);

      this.xAxis.children.unshift(
        am5.Label.new(this.root, {
          text: xTitle, // Tìtulo
          fontSize: 16,
          fontWeight: "bold",
          y: 10, // Distancia desde el eje
          x: am5.p50, // Centrado horizontalmente
          centerX: am5.p50, // Asegura el centrado horizontal
          centerY: am5.p50, // Asegura el centrado vertical
          fill: am5.color(0x74786E), // Color del texto
        })
      );
    }

    // Configura el título para el eje Y
    if(this.yAxisTitle){
      const yearMatch = extractYearFromString(this.yAxisTitle);
      let nameBase = yearMatch ? this.yAxisTitle.replace(yearMatch, '').trim() : this.yAxisTitle; // Elimina el año, si no encuentra año, asigna el título original
      let yTitle = this.translateService.instant(`CHARTS.${nameBase}`);

      this.yAxis.children.unshift(
        am5.Label.new(this.root, {
          text: yTitle, // Título
          rotation: -90, // Rotación para texto vertical
          fontSize: 16,
          fontWeight: "bold",
          x: 5, // Distancia desde el eje
          y: am5.p50, // Centrado verticalmente
          centerX: am5.p50, // Asegura el centrado horizontal
          fill: am5.color(0x74786E), // Color del texto
        })
      );
    }

    // Crea una serie de columnas con configuraciones específicas
    let series = this.chart.series.push(am5xy.ColumnSeries.new(this.root, {
      calculateAggregates: true,  // Activa el cálculo de agregados
      stroke: am5.color(0xffffff),  // Define el color del borde
      clustered: false,  // Desactiva la agrupación de columnas
      xAxis: this.xAxis,  // Asocia el eje X
      yAxis: this.yAxis,  // Asocia el eje Y
      categoryXField: this.categoryXField,  // Define el campo para el eje X
      categoryYField: this.categoryYField,  // Define el campo para el eje Y
      valueField: "value"  // Define el campo de valor
    }));

    // Configura el estilo de las columnas
    series.columns.template.setAll({
      tooltipText: "{categoryX}: {value}",  // Muestra el valor en el tooltip
      strokeOpacity: 1,  // Opacidad del borde
      strokeWidth: 2,  // Grosor del borde
      width: am5.percent(100),  // Ancho al 100%
      height: am5.percent(100)  // Altura al 100%
    });

    series.set("heatRules", [{  // Establece reglas de color para la serie de columnas
      target: series.columns.template,  // Aplica la regla a las columnas de la serie
      min: am5.color(this.minColor),  // Define el color para el valor mínimo
      max: am5.color(this.maxColor),  // Define el color para el valor máximo
      minValue: this.valueRangeMin && this.valueRangeMin,  // Valor mínimo para la escala de colores
      maxValue: this.valueRangeMax && this.valueRangeMax,  // Valor máximo para la escala de colores
      dataField: "value",  // Campo de datos que se usará para la regla de color
      key: "fill"  // Establece la propiedad "fill" para aplicar el color
    }]);

    // Línea con la escala de colores (aparece debajo del gráfico)
    let heatLegend = this.chart.bottomAxesContainer.children.push(am5.HeatLegend.new(this.root, {  // Crea y agrega la leyenda de calor
      orientation: "horizontal",  // Establece la orientación horizontal
      startColor: am5.color(this.maxColor),  // Asigna el color inicial de la leyenda
      endColor: am5.color(this.minColor),  // Asigna el color final de la leyenda
    }));

    // Evento para mostrar el valor en el heatLegend cuando el puntero pasa sobre una columna
    series.columns.template.events.on("pointerover", function(event:any) {
      let di = event.target.dataItem;
      if (di) {
        heatLegend.showValue(di.get("value", 0));  // Muestra el valor en el heatLegend
      }
    });

    // Evento para actualizar los valores del heatLegend cuando los datos son validados
    series.events.on("datavalidated", function() {
      heatLegend.set("startValue", series.getPrivate("valueHigh"));  // Establece el valor mínimo
      heatLegend.set("endValue", series.getPrivate("valueLow"));  // Establece el valor máximo
    });

    series.data.setAll(this.chartData); // Establece todos los datos de la serie usando los datos proporcionados en 'this.chartData'

    // Actualiza los datos en los ejes
    this.yAxis.data.setAll(this.uniqueValues(this.categoryYField).map(value => ({ [this.categoryYField]: value })));
    this.xAxis.data.setAll(this.uniqueValues(this.categoryXField).map(value => ({ [this.categoryXField]: value })));

    series.appear(2000); // Hace que la serie aparezca en el gráfico con una animación que dura 2000 ms (2 segundos)
    this.chart.appear(2000, 100); // Hace que el gráfico aparezca con una animación que dura 2000 ms (2 segundos) y un retraso de 100 ms (100 milisegundos)

    // Agrega un cursor al gráfico
    this.chart.set( 'cursor', am5xy.XYCursor.new(this.root, {})); // Permite configurar el comportamiento del cursor

    // Agrega las opciones para la exportación
    this.exporting = am5plugins_exporting.Exporting.new(this.root, {
      filePrefix: `${this.fileName}`, // Nombre de los archivos exportados
      menu: am5plugins_exporting.ExportingMenu.new(this.root, {}), // Añade un menú de opciones de exportación en el gráfico
      dataSource: this.translateDataKeysService.translateKeys(this.chartData, 'CHARTS'), // Especifica los datos que se exportarán, usando 'chartData' como fuente
      htmlOptions: {
        disabled: true, // Desactiva la exportación en formato HTML
      },
      jsonOptions: {
        disabled: true, // Desactiva la exportación en formato JSON
      }
    });
  }

  private updateChartData() {
    if (this.chart) {
      this.chart.series.each((series) => {
        // Recalcular altura cuando los datos cambien
        this.calculateChartHeight();
        // Actualiza los datos de la serie
        series.data.setAll(this.chartData);

        // Actualiza los datos en los ejes
        this.yAxis.data.setAll(this.uniqueValues(this.categoryYField).map(value => ({ [this.categoryYField]: value })));
        this.xAxis.data.setAll(this.uniqueValues(this.categoryXField).map(value => ({ [this.categoryXField]: value })));
      });

      // Actualiza la fuente de datos para la exportación
      if (this.exporting) {
        this.exporting.set("dataSource", this.translateDataKeysService.translateKeys(this.chartData, 'CHARTS'));
      }
    }
  }
}
